import { useState, type AnchorHTMLAttributes } from 'react';
import { Briefcase, CalendarBlank, Sparkle, User } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { EntityType, assistantReplyChrome } from '@ruta/shared';
import type { ChatMessage, NavigationAction } from '@/contexts/ChatContext';
import type { AssistantEntityReference } from '@/lib/api/assistantMentions';
import { MessageResponse } from '@/components/ai-elements/message';

// A stat/entity the assistant hot-linked (RUT-2683). Internal targets (a same-app
// path like /customers/xyz or /revenue) render as a dotted-underline link that
// navigates in-app on click; the link title becomes a hover tooltip explaining the
// number. Anything else falls back to a normal link.
export function makeAssistantLink(onNavigate?: (path: string) => void) {
  return function AssistantLink({ href, title, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
    const isInternal = !!href && href.startsWith('/');
    if (!isInternal) {
      return (
        <a href={href} title={title} target="_blank" rel="noreferrer" {...rest}>
          {children}
        </a>
      );
    }
    return (
      <a
        href={href}
        title={title}
        onClick={(e) => {
          e.preventDefault();
          onNavigate?.(href!);
        }}
        className="underline decoration-dotted decoration-white/50 underline-offset-2 hover:decoration-white cursor-pointer"
        {...rest}
      >
        {children}
      </a>
    );
  };
}

function refIcon(entityType: string) {
  const props = { className: 'h-3 w-3 shrink-0 opacity-80' };
  if (entityType === EntityType.VISIT) return <CalendarBlank {...props} />;
  if (entityType === EntityType.CUSTOMER) return <User {...props} />;
  return <Briefcase {...props} />;
}

function UserMessageBubble({
  message,
  onReferencePress,
}: {
  message: ChatMessage;
  onReferencePress?: (ref: AssistantEntityReference) => void;
}) {
  const [briefOpen, setBriefOpen] = useState(false);
  const summary = message.displayContent?.trim();
  const hasHiddenBrief = Boolean(summary && summary !== message.content);
  const visible = hasHiddenBrief ? summary! : message.content;

  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary px-3 py-2 text-primary-foreground">
        <div className="whitespace-pre-wrap text-[13px] leading-snug">{visible}</div>
        {hasHiddenBrief && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={() => setBriefOpen((v) => !v)}
              aria-expanded={briefOpen}
              className="text-[10.5px] font-medium text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            >
              {briefOpen ? 'Hide brief' : 'Show brief'}
            </button>
            {briefOpen && (
              <div className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap text-[11px] leading-snug text-primary-foreground/75 border-t border-white/15 pt-1.5">
                {message.content}
              </div>
            )}
          </div>
        )}
        {message.references && message.references.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.references.map((r) => (
              <button
                key={`${r.entityType}:${r.entityId}`}
                type="button"
                disabled={!onReferencePress}
                onClick={() => onReferencePress?.(r)}
                title={onReferencePress ? `Open ${r.displayName ?? 'item'}` : undefined}
                className="inline-flex items-center gap-1 rounded-full bg-white/15 px-1.5 py-0.5 text-[10.5px] text-primary-foreground max-w-[160px] transition-colors hover:bg-white/25 disabled:cursor-default disabled:hover:bg-white/15"
              >
                {refIcon(r.entityType)}
                <span className="truncate">{r.displayName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// One Ruta AI chat bubble. Mirrors the mobile design: user turns are a green
// right-aligned bubble carrying their @-references; assistant turns are a muted
// bordered bubble with markdown, followed by navigation / suggestion / confirm
// actions. Shared so every web assistant surface renders messages identically.
export function AssistantMessageBubble({
  message,
  isGenerating,
  onNavigate,
  onSuggest,
  onConfirm,
  onCancel,
  onReferencePress,
  onLinkNavigate,
  onApplyDraft,
  applyDraftLabel = 'Use this',
}: {
  message: ChatMessage;
  isGenerating?: boolean;
  onNavigate: (action: NavigationAction) => void;
  onSuggest: (prompt: string) => void;
  onConfirm: (message: ChatMessage) => void;
  onCancel: (message: ChatMessage) => void;
  onReferencePress?: (ref: AssistantEntityReference) => void;
  onLinkNavigate?: (path: string) => void;
  /** Hand this reply's text back to a host field (e.g. customer message composer). */
  onApplyDraft?: (text: string) => void;
  applyDraftLabel?: string;
}) {
  if (message.role === 'user') {
    return (
      <UserMessageBubble message={message} onReferencePress={onReferencePress} />
    );
  }

  const hasAnswer = !!message.content?.trim();
  // Answer text or a settled turn both count as done so a prior reply does
  // not grow a "Looking up…" footer while a new question is streaming.
  const { statusLine, groundingFooter } = assistantReplyChrome({
    done: hasAnswer || !isGenerating,
    hasAnswer,
    activityLabel: message.activityLabel ?? null,
    toolCalls: message.toolCalls,
    toolNames: message.toolNames,
  });

  return (
    <div className="is-assistant flex flex-col items-start gap-2">
      {(message.content?.trim() || statusLine) && (
        <div className="max-w-[92%] rounded-2xl rounded-bl-md border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-[13px] text-white/85">
          {message.content?.trim() ? (
            <MessageResponse components={{ a: makeAssistantLink(onLinkNavigate) }}>
              {message.content}
            </MessageResponse>
          ) : (
            <div className="flex items-center gap-2">
              <Sparkle weight="fill" className="h-[13px] w-[13px] text-white" />
              <span className="text-[13px] font-medium text-white/80">{statusLine}</span>
            </div>
          )}
        </div>
      )}

      {onApplyDraft && !message.failed && !!message.content?.trim() && !isGenerating && (
        <button
          type="button"
          onClick={() => onApplyDraft(message.content.trim())}
          className="px-2.5 py-1 text-[11px] rounded-md font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {applyDraftLabel}
        </button>
      )}

      {message.navigationActions && message.navigationActions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {message.navigationActions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(a)}
              className={cn(
                'px-2.5 py-1 text-[11px] rounded-md font-medium transition-colors',
                a.variant === 'primary'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'border border-white/20 text-white/80 hover:bg-white/10 hover:text-white',
              )}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {message.suggestions && message.suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {message.suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onSuggest(s.prompt)}
              disabled={isGenerating || message.pendingConfirmation}
              className="px-2.5 py-1 text-[11px] rounded-full border border-white/15 text-white/75 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {message.pendingRetry && message.pendingReplay && (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onConfirm(message)}
            disabled={isGenerating}
            className="px-2.5 py-1 text-[11px] rounded-md font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      )}

      {message.pendingConfirmation && !message.pendingRetry && (
        <div className="flex flex-col gap-1.5">
          {message.confirmationPrompt && (
            <p className="text-[11px] text-white/70 leading-snug">{message.confirmationPrompt}</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => onConfirm(message)}
              disabled={isGenerating}
              className="px-2.5 py-1 text-[11px] rounded-md font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => onCancel(message)}
              disabled={isGenerating}
              className="px-2.5 py-1 text-[11px] rounded-md font-medium border border-white/20 text-white/80 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {groundingFooter && !message.failed && (
        <div className="px-1 text-[10px] text-white/40">
          {groundingFooter}
        </div>
      )}
    </div>
  );
}
